/** Firestore `in` accepts at most 30 values. */
export const PHONE_SEARCH_MAX_VALUES = 30;

/** Partial input shorter than this is not resolved to stored values (too many unrelated matches). */
export const PHONE_SEARCH_MIN_PARTIAL_DIGITS = 6;

export type PhoneSearchCondition = {
  operator: "==" | "in";
  value: string | string[];
};

/**
 * Firestore condition for a phone search. A typed number ("012-345 6789") is matched as its
 * canonical E.164 form, then as each stored value it resolved to (`matchedValues`, e.g. a
 * multi-number field "+60102223333; +60123456789" containing it), and finally as the raw text so
 * a non-parseable query or a row still holding a legacy value is found too.
 */
export function phoneSearchCondition(
  input: string,
  matchedValues: string[] = [],
  maxValues: number = PHONE_SEARCH_MAX_VALUES,
): PhoneSearchCondition {
  const raw = input.trim();
  const values = [
    ...new Set(
      [normalizePhoneList(raw), ...matchedValues, raw].filter(Boolean),
    ),
  ].slice(0, Math.max(1, maxValues));

  if (values.length > 1) return { operator: "in", value: values };
  return { operator: "==", value: values[0] ?? raw };
}

/**
 * Keeps the stored phone fields that contain at least one of `searchPhones` as a whole number,
 * so a substring hit ("+6012345678" inside "+60123456789") is not treated as a match.
 */
export function filterPhoneListsContaining(
  searchPhones: string[],
  candidates: string[],
): string[] {
  return candidates.filter((candidate) =>
    parsePhoneList(candidate).phones.some((phone) =>
      searchPhones.includes(phone),
    ),
  );
}
